/**
 * API contracts between capture clients, business backends and the KYC backend.
 * ARCHITECTURE.md §3 (session service), §21 (upload), §22 (trust boundary).
 */
import { z } from 'zod';
import { CAPTURE_STAGES } from './stages.js';
import { ASSURANCE_LEVELS, STAGE_INVALIDATION_REASONS } from './session.js';
import { MAX_CLIENT_BEST_FRAME_HINTS } from './media-validation.js';
export const captureStageSchema = z.enum(CAPTURE_STAGES);
// ---------------------------------------------------------------------------
// Business API (server-to-server, x-api-key)
// ---------------------------------------------------------------------------
export const createSessionRequestSchema = z.object({
    /** Business system's own reference (customer id, application id…). */
    externalRef: z.string().min(1).max(256).optional(),
    documentProfileId: z.string().min(1).max(128).optional(),
    /** Defaults to WEB_LOW_ASSURANCE until native SDKs ship — §2. */
    assuranceLevel: z.enum(ASSURANCE_LEVELS).default('WEB_LOW_ASSURANCE'),
    /** Session validity window. */
    ttlMinutes: z.number().int().min(5).max(24 * 60).default(60),
});
export const createSessionResponseSchema = z.object({
    sessionId: z.uuid(),
    /** Link to send over SMS/WhatsApp/Email — §2. */
    captureUrl: z.url(),
    /** One-time token embedded in the link; exchanged via /capture/claim. */
    claimToken: z.string(),
    expiresAt: z.iso.datetime(),
});
// ---------------------------------------------------------------------------
// Capture API (Bearer capture token)
// ---------------------------------------------------------------------------
export const claimSessionRequestSchema = z.object({
    claimToken: z.string().min(16),
    device: z
        .object({
        platform: z.enum(['android', 'ios', 'web']),
        model: z.string().max(256).optional(),
        osVersion: z.string().max(64).optional(),
        appVersion: z.string().max(64).optional(),
        userAgent: z.string().max(1024).optional(),
    })
        .optional(),
});
/** §19 — client manifest accompanying each uploaded video. */
export const clientManifestSchema = z.object({
    bestFrameTimestampsMs: z
        .array(z.number().min(0).max(60_000))
        .max(MAX_CLIENT_BEST_FRAME_HINTS)
        .default([]),
    /** Client-side quality metadata — diagnostics only, never trusted (§22). */
    qualityMetadata: z.record(z.string(), z.unknown()).optional(),
});
export const uploadInitRequestSchema = z.object({
    declaredSizeBytes: z.number().int().positive(),
    mimeType: z.enum(['video/mp4', 'video/quicktime', 'video/webm']),
    /** Client-side SHA-256 (hex); verified against the server-side hash on complete. */
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/i)
        .optional(),
    clientManifest: clientManifestSchema.default({ bestFrameTimestampsMs: [] }),
});
export const uploadInitResponseSchema = z.object({
    mediaId: z.uuid(),
    uploadToken: z.string(),
    chunkSizeBytes: z.number().int().positive(),
    totalChunks: z.number().int().positive(),
    expiresAt: z.iso.datetime(),
});
export const uploadCompleteRequestSchema = z.object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/i),
    totalChunks: z.number().int().positive(),
});
export const stageEventRequestSchema = z.object({
    type: z.enum(['stage_started', 'ready_gate_passed', 'recording_started', 'retake', 'invalidated']),
    reason: z.enum(STAGE_INVALIDATION_REASONS).optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Error envelope
// ---------------------------------------------------------------------------
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
//# sourceMappingURL=api.js.map